import { Component, Element, h, Host, Method, Prop, State, Watch } from "@stencil/core";
import { applySchemeDelegate, ColorableComponent, setCssVariablesDelegate } from "../../common/colorable-component";
import { TextSchemeDefinition, TextSchemeName, TextStyle } from "./constants/text-types";
import { TEXT_SCHEME_CONFIG, TEXT_SCHEME_MAP } from "./constants/text-constants";
import pfx from "../../utils/style-class-prefix-helper";
import { EonColor, TextSchemeConfiguration } from "../../constants/colors";
import { BackgroundColor } from "../background/constants/background-types";
import { StyledComponent } from "../../decorators/styled-component";
// import { InitLogging } from "../../decorators/init-logging";
// import { CaptionSchemeDefinition } from "../caption/constants/caption-types";
// import { CAPTION_SCHEME_CONFIG, CAPTION_SCHEME_MAP } from "../caption/constants/caption-constants";

/**
 * @slot info-icon - Holds a single eon-ui-info-icon element
 * @slot [unnamed] - holds any number of HTML elements.
 */
@Component({
  tag: "eon-ui-text",
  styleUrl: "text.scss",
  shadow: true
})
export class Text implements ColorableComponent<TextSchemeConfiguration<TextSchemeDefinition>> {
  // @InitLogging() sentryTransaction;
  schemeConfig: TextSchemeConfiguration<TextSchemeDefinition> = TEXT_SCHEME_CONFIG;
  schemeMap = TEXT_SCHEME_MAP;

  /**
   * The components active scheme. If {@link scheme} is set, it will be set to the corresponding scheme.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @State() activeScheme: TextSchemeDefinition;
  @State() isInfoIconAvailable: boolean;

  @StyledComponent() component: any;
  @Element() hostElement: HTMLEonUiTextElement;

  /**
   * The name of the color scheme of the component.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @Prop({ reflect: true }) scheme: TextSchemeName;

  /**
   * The style for the Text
   */
  @Prop() textStyle: TextStyle = "copy";

  /**
   * The color
   */
  // eslint-disable-next-line @stencil-community/reserved-member-names
  @Prop() color: EonColor;

  /**
   * Decide whether the internal component tag should be p (read by screen reader) or span.
   * @default "p"
   */
  @Prop() internalTag: "p" | "span" = "span";

  /**
   * Decide whether the internal component display should be other than "inline".
   */
  @Prop() internalDisplayStyle: string = "inline";

  /**
   * The font-weight style
   */
  @Prop() fontWeight: string;

  /**
   * Specifies the label to use for accessibility.
   */
  @Prop({ attribute: "a11y-aria-label", mutable: true }) a11yAriaLabel: string;

  /**
   * This method can be called to re-trigger the automatic background color detection
   */
  @Method()
  async updateScheme(backgroundColor?: BackgroundColor) {
    applySchemeDelegate(this, backgroundColor);
  }

  /**
   * The display style for the Text,
   * to display it either as inline-flex or inline-block
   * Possible values: "flex", "block"
   * Default value: "flex"
   */
  @Prop() displayStyle: "flex" | "block" = "flex";

  componentWillLoad() {
    this.applyScheme();
    this.isInfoIconAvailable = !!this.hostElement.querySelector('[slot="info-icon"]');
  }

  componentDidLoad() {
    this.setCssVariables();
    this.setColor();
  }

  @Watch("scheme")
  applyScheme() {
    applySchemeDelegate(this);
  }

  @Watch("activeScheme")
  setCssVariables(): void {
    setCssVariablesDelegate(this);
  }

  @Watch("color")
  setColor() {
    if (!this.scheme && this.color) {
      this.hostElement.style.setProperty(`--color`, `var(--${this.color})`);
    }
  }

  render() {
    const Tag = this.internalTag as keyof JSX.IntrinsicElements;

    return (
      <Host>
        <Tag
          class={pfx({
            ["text-wrapper"]: true,
            [this.textStyle]: true,
            ["info-icon-display"]: this.isInfoIconAvailable,
            [this.displayStyle]: true
          })}
          style={{ fontWeight: this.fontWeight, display: this.internalDisplayStyle }}
          {...(this.a11yAriaLabel ? { "aria-label": this.a11yAriaLabel } : {})}
        >
          <slot></slot>
          {this.isInfoIconAvailable && (
            <span class={pfx("text-info-icon")}>
              <slot name="info-icon" />
            </span>
          )}
        </Tag>
      </Host>
    );
  }
}
